import { createHelpAPI } from "../api/apiFactory";
import { log } from "../core/logger";
import type { CategoryWithArticles, FAQ, HelpArticle, HelpCategory, HelpSearchResults } from "../types/HelpArticle";
import type { CreateArticleParams, CreateCategoryParams, CreateFAQParams, IHelpAPI, UpdateArticleParams, UpdateCategoryParams, UpdateFAQParams } from "../api/helpApi";

/**
 * Service layer for help content
 * Provides higher-level abstractions over the raw API
 */
export class HelpService {
  public api: IHelpAPI;

  constructor(api?: IHelpAPI) {
    this.api = api || createHelpAPI();
  }

  /**
   * Get all active help categories
   */
  async getCategories(): Promise<HelpCategory[]> {
    try {
      return await this.api.fetchCategories();
    } catch (error) {
      log("HelpService", "Failed to get categories", error);
      throw error;
    }
  }

  /**
   * Get category with its articles by slug
   */
  async getCategoryWithArticles(slug: string): Promise<CategoryWithArticles> {
    try {
      return await this.api.fetchCategoryBySlug(slug);
    } catch (error) {
      log("HelpService", "Failed to get category with articles", error);
      throw error;
    }
  }

  /**
   * Get article by slug
   */
  async getArticle(slug: string): Promise<HelpArticle> {
    try {
      return await this.api.fetchArticleBySlug(slug);
    } catch (error) {
      log("HelpService", "Failed to get article", error);
      throw error;
    }
  }

  /**
   * Search help content (articles and FAQs)
   */
  async search(query: string): Promise<HelpSearchResults> {
    try {
      if (query.trim().length < 2) {
        return {
          articles: [],
          faqs: [],
          totalResults: 0,
        };
      }
      return await this.api.searchHelp(query.trim());
    } catch (error) {
      log("HelpService", "Failed to search help content", error);
      throw error;
    }
  }

  /**
   * Get all FAQs
   */
  async getFAQs(): Promise<FAQ[]> {
    try {
      return await this.api.fetchFAQs();
    } catch (error) {
      log("HelpService", "Failed to get FAQs", error);
      throw error;
    }
  }

  // ==================== ADMIN METHODS ====================

  /**
   * Get all articles (including unpublished) - Admin only
   */
  async getAllArticles(): Promise<HelpArticle[]> {
    try {
      return await this.api.getAllArticles();
    } catch (error) {
      log("HelpService", "Failed to get all articles", error);
      throw error;
    }
  }

  /**
   * Create a new category - Admin only
   */
  async createCategory(params: CreateCategoryParams): Promise<HelpCategory> {
    try {
      return await this.api.createCategory(params);
    } catch (error) {
      log("HelpService", "Failed to create category", error);
      throw error;
    }
  }

  /**
   * Update a category - Admin only
   */
  async updateCategory(id: string, params: UpdateCategoryParams): Promise<HelpCategory> {
    try {
      return await this.api.updateCategory(id, params);
    } catch (error) {
      log("HelpService", "Failed to update category", error);
      throw error;
    }
  }

  /**
   * Create a new article - Admin only
   */
  async createArticle(params: CreateArticleParams): Promise<HelpArticle> {
    try {
      return await this.api.createArticle(params);
    } catch (error) {
      log("HelpService", "Failed to create article", error);
      throw error;
    }
  }

  /**
   * Update an article - Admin only
   */
  async updateArticle(id: string, params: UpdateArticleParams): Promise<HelpArticle> {
    try {
      return await this.api.updateArticle(id, params);
    } catch (error) {
      log("HelpService", "Failed to update article", error);
      throw error;
    }
  }

  /**
   * Delete an article - Admin only
   */
  async deleteArticle(id: string): Promise<void> {
    try {
      return await this.api.deleteArticle(id);
    } catch (error) {
      log("HelpService", "Failed to delete article", error);
      throw error;
    }
  }

  /**
   * Upload an inline article image, returns the public URL - Admin only
   */
  async uploadArticleImage(file: File): Promise<string> {
    try {
      return await this.api.uploadArticleImage(file);
    } catch (error) {
      log("HelpService", "Failed to upload article image", error);
      throw error;
    }
  }

  /**
   * Create a new FAQ - Admin only
   */
  async createFAQ(params: CreateFAQParams): Promise<FAQ> {
    try {
      return await this.api.createFAQ(params);
    } catch (error) {
      log("HelpService", "Failed to create FAQ", error);
      throw error;
    }
  }

  /**
   * Update a FAQ - Admin only
   */
  async updateFAQ(id: string, params: UpdateFAQParams): Promise<FAQ> {
    try {
      return await this.api.updateFAQ(id, params);
    } catch (error) {
      log("HelpService", "Failed to update FAQ", error);
      throw error;
    }
  }
}
